<template>
  <div class="advert-week">
    <div class="left">
      <el-checkbox v-model="allChecked" :indeterminate="allIndeterminate" @change="onToggleAll">{{
        $t("adverput.all")
      }}</el-checkbox>
      <div class="days">
        <div v-for="(d, i) in days" :key="d" class="day-row">
          <el-checkbox
            v-model="daysChecked[i]"
            :indeterminate="daysIndeterminate[i]"
            @change="val => onToggleDay(i, val)"
            >{{ d }}</el-checkbox
          >
        </div>
      </div>
    </div>
    <div class="right">
      <div class="time-header">
        <div v-for="h in 24" :key="h" class="time">{{ formatHour(h - 1) }}</div>
      </div>
      <div class="grid">
        <div v-for="(row, i) in schedule" :key="i" class="grid-row">
          <div v-for="(v, j) in row" :key="j" class="cell" :class="{ active: v }" @click="toggleCell(i, j)"></div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: "AdvertWeek",
    props: {
      value: {
        type: Array,
        default: () => []
      }
    },
    data() {
      return {
        days: [
          this.$t("adverput.z11"),
          this.$t("adverput.z22"),
          this.$t("adverput.z33"),
          this.$t("adverput.z44"),
          this.$t("adverput.z55"),
          this.$t("adverput.z66"),
          this.$t("adverput.z77")
        ],
        schedule: Array.from({ length: 7 }, () => Array(24).fill(false)),
        daysChecked: Array(7).fill(false),
        daysIndeterminate: Array(7).fill(false),
        allChecked: false,
        allIndeterminate: false
      };
    },
    watch: {
      value: {
        immediate: true,
        handler(v) {
          if (
            Array.isArray(v) &&
            v.every(item => typeof item === "object" && "week" in item && "start" in item && "end" in item)
          ) {
            this.schedule = this.parseScheduleFromNewFormat(v);
            this.syncDayChecks();
            this.syncAllCheck();
          } else if (Array.isArray(v) && v.length === 7 && v.every(r => Array.isArray(r) && r.length === 24)) {
            // Fallback for old format (7x24 boolean array)
            this.schedule = v.map(r => r.slice());
            this.syncDayChecks();
            this.syncAllCheck();
          }
        }
      }
    },
    methods: {
      formatHour(h, type = "display") {
        const s = h.toString().padStart(2, "0");
        if (type === "display") {
          return `${s}:00`;
        } else if (type === "end") {
          return `${s}:59:59`;
        }
        return `${s}:00:00`;
      },
      parseScheduleFromNewFormat(newFormatValue) {
        const schedule = Array.from({ length: 7 }, () => Array(24).fill(false));
        if (!Array.isArray(newFormatValue)) {
          console.log("parseScheduleFromNewFormat: newFormatValue is not an array", newFormatValue);
          return schedule;
        }

        newFormatValue.forEach(item => {
          const weekIndex = item.week - 1; // Convert 1-indexed week to 0-indexed
          if (weekIndex >= 0 && weekIndex < 7) {
            const startHour = parseInt(item.start.split(":")[0], 10);
            let endHour = parseInt(item.end.split(":")[0], 10);
            const endMinute = parseInt(item.end.split(":")[1], 10);

            // If the end minute is 59, it means the hour itself is included, so we increment endHour for the loop
            if (endMinute === 59) {
              endHour++;
            }

            for (let h = startHour; h < endHour; h++) {
              if (h >= 0 && h < 24) {
                schedule[weekIndex][h] = true;
              }
            }
          }
        });
        return schedule;
      },
      convertToNewFormat(schedule) {
        const newFormatValue = [];
        schedule.forEach((daySchedule, weekIndex) => {
          for (let h = 0; h < 24; h++) {
            if (daySchedule[h]) {
              newFormatValue.push({
                week: weekIndex + 1,
                start: this.formatHour(h, "start"),
                end: this.formatHour(h, "end")
              });
            }
          }
        });
        return newFormatValue;
      },
      emitChange() {
        const newFormatValue = this.convertToNewFormat(this.schedule);
        this.$emit("input", newFormatValue);
        this.$emit("change", newFormatValue);
      },
      syncDayChecks() {
        this.daysChecked = this.schedule.map(r => r.every(x => x));
        this.daysIndeterminate = this.schedule.map(r => r.some(x => x) && !r.every(x => x));
      },
      syncAllCheck() {
        const allHours = this.schedule.flat();
        this.allChecked = allHours.every(x => x);
        this.allIndeterminate = allHours.some(x => x) && !this.allChecked;
      },
      onToggleAll(val) {
        this.schedule = this.schedule.map(() => Array(24).fill(!!val));
        this.daysChecked = this.daysChecked.map(() => !!val);
        this.syncAllCheck();
        this.emitChange();
        console.log(this.schedule);
      },
      onToggleDay(i, val) {
        this.schedule.splice(i, 1, Array(24).fill(!!val));
        this.syncDayChecks();
        this.syncAllCheck();
        this.emitChange();
      },
      toggleCell(i, j) {
        const row = this.schedule[i].slice();
        row[j] = !row[j];
        this.schedule.splice(i, 1, row);
        this.syncDayChecks();
        this.syncAllCheck();
        this.emitChange();
      }
    }
  };
</script>

<style lang="scss" scoped>
  .advert-week {
    display: flex;
    align-items: flex-start;
    background: #f7f7f7;
    padding: 8px 6px;
    overflow: auto;
    .left {
      width: 72px;
      display: flex;
      flex-direction: column;
      align-items: flex-start;
      margin-right: 8px;
      .header-placeholder {
        height: 32px;
        width: 100%;
      }
      .days {
        display: flex;
        flex-direction: column;
        gap: 6px;
        margin-top: 6px;
      }
      .day-row {
        height: 32px;
        display: flex;
        align-items: center;
      }
    }
    .right {
      .time-header {
        display: grid;
        grid-template-columns: repeat(24, 40px);
        gap: 2px;
        margin-bottom: 6px;
        .time {
          font-size: 12px;
          color: #666;
          text-align: center;
        }
      }
      .grid {
        display: grid;
        grid-auto-rows: 32px;
        row-gap: 6px;
        .grid-row {
          display: grid;
          grid-template-columns: repeat(24, 40px);
          column-gap: 2px;
        }
        .cell {
          width: 40px;
          height: 36px;
          background: #fff;
          border-radius: 2px;
          cursor: pointer;
        }
        .cell.active {
          background: #c3e0fe;
        }
      }
    }
  }
</style>
